import { faFax } from '@fortawesome/free-solid-svg-icons';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useAxios from '../../../hooks/useAxios';
import useAxiosHook from '../../../hooks/useAxiosHook';
import { useEndpointGuard } from '../../../hooks/useEndpointGuard';
import usePageTitle from '../../../hooks/usePageTitle';
import useRouteDialog from '../../../hooks/useRouteDialog';
import useTableColumns from '../../../hooks/useTableColumns';
import useTableState from '../../../hooks/useTableState';
import { WithPagination } from '../../../types/api';
import * as transportEntitiesGuard from '../../../utils/constants/auth/transportEntities';
import {
  RoutePaths,
  constructIdRoute,
} from '../../../utils/constants/routePaths';
import { sequential } from '../../../utils/helpers/functions';
import { queryString } from '../../../utils/helpers/http';
import { httpQueryObject } from '../../../utils/helpers/misc';
import Table from '../../DataTable/Table/Table';
import Flex from '../../layout/flex/Flex';
import MainContent from '../../layout/flex/MainContent';
import Sidebar from '../../layout/flex/Sidebar';
import Filters from '../Components/Filters/Filters';
import HeaderPages from '../Components/HeaderPages/HeaderPages';
import BarcodeDialog from './Dialogs/BarcodeDialog';
import CreateDialog from './Dialogs/CreateDialogTransportEntities/CreateDialogTransportEntities';
import DeleteDialog from './Dialogs/Delete/DeleteDialog';
import QRDialog from './Dialogs/QRDialog';
import ViewDialog from './Dialogs/View/ViewDialog';
import {
  TransportEntitiesType,
  additionalColumnProperties,
  getColumnHeadersMap,
  tableStorageKey,
} from './TransportEntities.functions';
import useTableFilters from './useTableFilters';

function TransportEntities(): JSX.Element {
  const { t } = useTranslation();
  usePageTitle(t('Transport entities'));
  const [action, setAction] = useState<string>('');
  const [caller, setCaller] =
    useState<'group-actions' | 'context-menu'>('context-menu');
  const [isQrDialogShown, setIsQrDialogShown] = useState(false);
  const [isBarcodeDialogShown, setIsBarcodeDialogShown] = useState(false);
  const readGuard = useEndpointGuard(
    transportEntitiesGuard.resourceTransportEntitiesRoutes
  );
  const createGuard = useEndpointGuard(
    transportEntitiesGuard.createTransportEntitiesRoutes
  );
  const editGuard = useEndpointGuard(
    transportEntitiesGuard.editTransportEntitiesRoutes
  );
  const deleteGuard = useEndpointGuard(
    transportEntitiesGuard.deleteTransportEntitiesRoutes
  );

  const {
    tableRef,
    page,
    setPage,
    limit,
    setLimit,
    sortField,
    sortOrder,
    selection,
    selectionMultiple,
    setSortField,
    setSortOrder,
    setSelection,
    setSelectionMultiple,
    contextMenuSelection,
    setContextMenuSelection,
  } = useTableState<TransportEntitiesType>(tableStorageKey);

  const { filters, headerFiltersCount, resetAllFilters, httpFiltersObj } =
    useTableFilters(page, setPage!, limit);

  const columnHeadersMap = useMemo(() => getColumnHeadersMap(t), [t]);

  const {
    data,
    error,
    isLoading,
    reload: reloadCollection,
  } = useAxiosHook<WithPagination<TransportEntitiesType[]>>(
    '/v2/transport-entities' + queryString(httpQueryObject(httpFiltersObj))
  );

  const { reload: editReload } = useAxios();

  const {
    show: showViewDialog,
    hide: hideViewDialog,
    isVisible: isViewDialogVisible,
  } = useRouteDialog(
    RoutePaths.TransportEntitiesPage,
    constructIdRoute(
      RoutePaths.ViewTransportEntities,
      contextMenuSelection?.id
    ),
    readGuard
  );

  const {
    show: showCreateDialog,
    hide: hideCreateDialog,
    isVisible: isCreateDialogVisible,
  } = useRouteDialog(
    RoutePaths.TransportEntitiesPage,
    RoutePaths.CreateTransportEntities,
    createGuard
  );

  const {
    show: showEditDialog,
    hide: hideEditDialog,
    isVisible: isEditDialogVisible,
  } = useRouteDialog(
    RoutePaths.TransportEntitiesPage,
    constructIdRoute(
      RoutePaths.EditTransportEntities,
      contextMenuSelection?.id
    ),
    editGuard
  );

  const {
    show: showDeleteDialog,
    hide: hideDeleteDialog,
    isVisible: isDeleteDialogVisible,
  } = useRouteDialog(
    RoutePaths.TransportEntitiesPage,
    constructIdRoute(
      RoutePaths.DeleteTransportEntities,
      contextMenuSelection?.id
    ),
    deleteGuard
  );

  const { selectedColumns, setSelectedColumns, columnOptions, columns } =
    useTableColumns(
      page,
      limit,
      'clients',
      columnHeadersMap,
      columnHeadersMap,
      (c) =>
        additionalColumnProperties(
          t,
          c as keyof typeof columnHeadersMap,
          setContextMenuSelection,
          setAction,
          setCaller,
          readGuard,
          editGuard,
          deleteGuard
        )
    );

  const finalColumns = useMemo<JSX.Element[]>(
    () => [
      ...columns,
      <Column
        key="action-column"
        header={t('Actions')}
        field="actions"
        frozen
        alignFrozen="right"
        {...additionalColumnProperties(
          t,
          'actions',
          setContextMenuSelection,
          setAction,
          setCaller,
          readGuard,
          editGuard,
          deleteGuard
        )}
      />,
    ],
    [
      columns,
      t,
      setContextMenuSelection,
      setAction,
      setCaller,
      readGuard,
      editGuard,
      deleteGuard,
    ]
  );

  useEffect(() => {
    if (action && contextMenuSelection) {
      setCaller('context-menu');

      if (action === 'view-details') {
        showViewDialog();
      }
      if (action === 'edit') {
        showEditDialog();
      }
      if (action === 'delete') {
        showDeleteDialog();
      }
      if (action === 'qr-code') {
        setIsQrDialogShown(true);
      }
      if (action === 'barcode') {
        setIsBarcodeDialogShown(true);
      }

      setAction('');
    }
  }, [
    action,
    contextMenuSelection,
    showViewDialog,
    showEditDialog,
    showDeleteDialog,
    setAction,
  ]);

  function handleCreateLogisticsRoute() {
    setContextMenuSelection(undefined);
    showCreateDialog();
  }

  function handelEditButton(data: TransportEntitiesType) {
    setContextMenuSelection(data);
    showEditDialog();
  }

  return (
    <div className="page transport-entities-page">
      <HeaderPages
        title={t('Transport entities')}
        subtitle={t('Transport entities management')}
        icon={faFax}
      >
        {createGuard && (
          <div>
            <Button
              label={t('Create transport entity')}
              icon="fas fa-plus"
              className="main-btn"
              onClick={handleCreateLogisticsRoute}
              data-cy="create-btn"
            />
          </div>
        )}
      </HeaderPages>

      <ViewDialog
        isShown={isViewDialogVisible}
        onHide={hideViewDialog}
        isEditBtnShown
        setIsEditDialog={handelEditButton}
      />

      <CreateDialog
        isEditDialog={isEditDialogVisible}
        isShown={isCreateDialogVisible || isEditDialogVisible}
        onHide={() => sequential(hideEditDialog, hideCreateDialog)}
        data={contextMenuSelection}
        reloadCollection={reloadCollection}
      />

      <DeleteDialog
        visible={isDeleteDialogVisible}
        data={
          caller === 'context-menu'
            ? contextMenuSelection
              ? [contextMenuSelection]
              : []
            : selectionMultiple
        }
        onHide={() => hideDeleteDialog()}
        reloadTransportEntities={reloadCollection}
        setSelectionMultiple={setSelectionMultiple}
        setContextMenuSelection={setContextMenuSelection}
      />

      <QRDialog
        data={contextMenuSelection ?? null}
        isShown={isQrDialogShown}
        onHide={() => setIsQrDialogShown(false)}
      />

      <BarcodeDialog
        data={contextMenuSelection ?? null}
        isShown={isBarcodeDialogShown}
        onHide={() => setIsBarcodeDialogShown(false)}
      />

      <Flex direction="column">
        <Sidebar>
          <Filters
            filters={filters}
            resetAllFilters={resetAllFilters}
            filterHeight={180}
          />
        </Sidebar>
        <MainContent>
          <Table
            tableRef={tableRef}
            columns={finalColumns}
            data={data}
            isLoading={isLoading}
            hasError={!!error}
            reload={reloadCollection}
            headerTitle=""
            filterHeight={180}
            headerFiltersCount={headerFiltersCount}
            onHeaderFiltersResetAllBtnClick={resetAllFilters}
            setPage={setPage}
            setLimit={setLimit}
            sortField={sortField}
            rows={limit}
            setSortField={setSortField}
            setSortOrder={setSortOrder}
            setSelection={setSelection}
            sortOrder={sortOrder}
            selection={selection}
            storageString={tableStorageKey}
            selectedColumns={selectedColumns}
            setSelectedColumns={setSelectedColumns}
            columnOptions={columnOptions}
            onRowDoubleClick={showViewDialog}
            contextMenuSelection={contextMenuSelection}
            setContextMenuSelection={setContextMenuSelection}
          />
        </MainContent>
      </Flex>
    </div>
  );
}

export default TransportEntities;
